"use client"

import { useEffect, useState, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Image,
  Button,
  VStack,
  Flex,
  useColorModeValue,
  Skeleton,
  SkeletonCircle,
  Badge,
  useBreakpointValue,
  Tooltip,
  useDisclosure,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  FormLabel,
  Stack,
  Tag,
  TagLabel,
  TagCloseButton,
  HStack
} from "@chakra-ui/react"
import { SearchIcon } from "@chakra-ui/icons"
import { FaFilter } from "react-icons/fa"
import { fetchCompanyListAsync, selectCompanies, selectTotalCompanies } from "./CompanySlice/CompanyListSlice"
import { Link } from "react-router-dom"
import { MapPinIcon, UsersIcon, GlobeAltIcon, BriefcaseIcon } from "@heroicons/react/24/outline"
import companylogo from "./logo1png.png"
import { EMPLOYER_COMPANY_LOGO_SAVE_URL } from "../common/URLs"
import { Footer } from "../footer/footer"

const CompanyCard = ({ company, base_url, onViewJobs }) => {
  const cardBg = useColorModeValue("white", "gray.800")
  const textColor = useColorModeValue("gray.600", "gray.300")
  const headingColor = useColorModeValue("gray.800", "white")
  const badgeBg = useColorModeValue("blue.50", "blue.900")
  const badgeColor = useColorModeValue("blue.600", "blue.200")

  return (
    <Box borderWidth="1px" borderRadius="lg" overflow="hidden" bg={cardBg}>
      <Box px={"6"}>
        <Flex alignItems="center" mb={4} py={"2"}>
          <Image
            borderRadius="full"
            boxSize="80px"
            src={company.company_logo ? `${base_url}${company.company_logo}` : companylogo}
            onError={(e) => (e.target.src = companylogo)}
            mr={4}
            objectFit="contain"
            border={"1"}
          />
          <VStack align="start" spacing={0}>
            <Link to={`/company-list/${company.uuid}`}>
              <Heading as="h3" size="md" color={headingColor}>
                {company?.company_name || "Company Name Not Mentioned"}
              </Heading>
            </Link>
            <Badge bg={badgeBg} color={badgeColor} fontSize="xs" px={2} py={1}>
              {company?.industry_type || "Industry Not Mentioned"}
            </Badge>
          </VStack>
        </Flex>
        <VStack align="start" spacing={0}>
          <Tooltip label="Company Type" placement="top">
            <Flex align="start">
              <Box as={BriefcaseIcon} w={5} h={5} color={textColor} mr={2} />
              <Text fontSize="sm" color={textColor}>
                {company.company_type || "Company Type"}
              </Text>
            </Flex>
          </Tooltip>
          <Tooltip label="Location" placement="top">
            <Flex align="start">
              <Box as={MapPinIcon} w={5} h={5} color={textColor} mr={2} />
              <Text fontSize="sm" color={textColor}>
                {company.city?.name || "City"}, {company.state?.name || "State"}, {company.country?.name || "Country"}
              </Text>
            </Flex>
          </Tooltip>
          <Tooltip label="Employee Count" placement="top">
            <Flex align="start">
              <Box as={UsersIcon} w={5} h={5} color={textColor} mr={2} />
              <Text fontSize="sm" color={textColor}>
                {company.number_of_employee || "N/A"} employees
              </Text>
            </Flex>
          </Tooltip>
          <Tooltip label="Website" placement="top">
            <Flex align="start" w="100%">
              <Box as={GlobeAltIcon} w={5} h={5} color={textColor} mr={2} />
              <Text fontSize="sm" color={textColor} isTruncated maxW="100%">
                {company.website || "Website not available"}
              </Text>
            </Flex>
          </Tooltip>
        </VStack>
      </Box>
      <Box borderTopWidth="1px" p={4}>
        <Link to={`/company-list/${company.uuid}`}>
          <Button
            width="full"
            colorScheme="blue"
            leftIcon={<Box as={BriefcaseIcon} w={5} h={5} />}
          >
            View {company.jobs_count} Open Jobs
          </Button>
        </Link>
      </Box>
    </Box>
  )
}

const CompanySkeleton = () => (
  <Box borderWidth="1px" borderRadius="lg" overflow="hidden" boxShadow="lg">
    <Box p={6}>
      <Flex mb={4}>
        <SkeletonCircle size="80px" mr={4} />
        <VStack align="start" flex={1} spacing={2}>
          <Skeleton height="24px" width="80%" />
          <Skeleton height="16px" width="60%" />
        </VStack>
      </Flex>
      <VStack align="start" spacing={3}>
        <HStack spacing={4} width="100%">
          <Skeleton height="16px" width="40%" />
          <Skeleton height="16px" width="40%" />
        </HStack>
        <Skeleton height="16px" width="70%" />
      </VStack>
    </Box>
    <Box borderTopWidth="1px" p={4}>
      <Skeleton height="40px" width="100%" />
    </Box>
  </Box>
)

export function CompanyList() {
  const dispatch = useDispatch()
  const companies = useSelector(selectCompanies)
  const totalCompanies = useSelector(selectTotalCompanies)

  const [selectedCompany, setSelectedCompany] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [industryFilter, setIndustryFilter] = useState("")
  const [companyTypeFilter, setCompanyTypeFilter] = useState("")
  const [sortBy, setSortBy] = useState("newest")
  const [filteredCompanies, setFilteredCompanies] = useState([])
  const [activeFilters, setActiveFilters] = useState([])
  const { isOpen, onOpen, onClose } = useDisclosure()

  const jobsPerPage = 16
  const [currentPage, setCurrentPage] = useState(1)
  const totalPages = Math.ceil(totalCompanies / jobsPerPage)

  // Use ref to track initial load and prevent duplicate API calls
  const initialLoad = useRef(true)
  const searchTimeoutRef = useRef(null)

  // Fetch companies only when currentPage changes
  useEffect(() => {
    if (initialLoad.current) {
      initialLoad.current = false
      dispatch(fetchCompanyListAsync(currentPage))
      return
    }

    // Throttle API calls to prevent 429 errors
    const timer = setTimeout(() => {
      dispatch(fetchCompanyListAsync(currentPage))
    }, 300)

    return () => clearTimeout(timer)
  }, [dispatch, currentPage])

  // Apply filters and sorting to companies
  useEffect(() => {
    if (!companies || !Array.isArray(companies)) return

    let result = [...companies]

    // Debounce search term filter
    if (searchTerm) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }

      searchTimeoutRef.current = setTimeout(() => {
        result = result.filter(
          (company) =>
            company?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            company?.industry_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            company?.company_type?.toLowerCase().includes(searchTerm.toLowerCase()),
        )
        setFilteredCompanies(result)
      }, 300)
    } else {
      // Apply other filters immediately
      if (industryFilter) {
        result = result.filter((company) => company.industry_type === industryFilter)
      }

      if (companyTypeFilter) {
        result = result.filter((company) => company.company_type === companyTypeFilter)
      }

      // Apply sorting
      switch (sortBy) {
        case "newest":
          result.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
          break
        case "oldest":
          result.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0))
          break
        case "a-z":
          result.sort((a, b) => a.company_name.localeCompare(b.company_name))
          break
        case "z-a":
          result.sort((a, b) => b.company_name.localeCompare(a.company_name))
          break
        case "most-jobs":
          result.sort((a, b) => (b.jobs_count || 0) - (a.jobs_count || 0))
          break
        default:
          break
      }

      setFilteredCompanies(result)
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [companies, searchTerm, industryFilter, companyTypeFilter, sortBy])

  // Update active filters
  useEffect(() => {
    const newActiveFilters = []

    if (industryFilter) {
      newActiveFilters.push({
        type: "industry",
        value: industryFilter,
        label: `Industry: ${industryFilter}`,
      })
    }

    if (companyTypeFilter) {
      newActiveFilters.push({
        type: "companyType",
        value: companyTypeFilter,
        label: `Company Type: ${companyTypeFilter}`,
      })
    }

    setActiveFilters(newActiveFilters)
  }, [industryFilter, companyTypeFilter])

  const removeFilter = (filterType) => {
    if (filterType === "industry") {
      setIndustryFilter("")
    } else if (filterType === "companyType") {
      setCompanyTypeFilter("")
    }
  }

  const clearAllFilters = () => {
    setIndustryFilter("")
    setCompanyTypeFilter("")
    setSearchTerm("")
  }

  // Extract unique industry types and company types for filters
  const industryTypes = Array.isArray(companies)
    ? [...new Set(companies.filter((company) => company.industry_type).map((company) => company.industry_type))]
    : []

  const companyTypes = Array.isArray(companies)
    ? [...new Set(companies.filter((company) => company.company_type).map((company) => company.company_type))]
    : []

  const columns = useBreakpointValue({ base: 1, md: 2, lg: 2 })

  const handleViewJobs = (company) => {
    setSelectedCompany(company)
    onOpen()
  }

  // Pagination handlers
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const FilterSidebar = () => {
    const cardBg = useColorModeValue("white", "gray.800")
    const textColor = useColorModeValue("gray.600", "gray.300")

    return (
      <Box
        borderWidth="1px"
        borderRadius="lg"
        overflow="hidden"
        bg={cardBg}
        boxShadow="md"
        p={5}
        height="fit-content"
        position={{ base: "static", lg: "sticky" }}
        className="top-20"
      >
        <Flex direction="column" gap={4}>
          <Flex justify="space-between" align="center" borderBottom="1px" borderColor="gray.200" pb={3}>
            <Text fontWeight="600" fontSize="md" color="gray.700">
              Filter Companies
            </Text>

            {activeFilters.length > 0 && (
              <Button size="sm" variant="ghost" colorScheme="blue" onClick={clearAllFilters} leftIcon={<FaFilter />}>
                Clear All
              </Button>
            )}
          </Flex>

          <VStack spacing={4} align="stretch">
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1} color="gray.600">
                Sort By
              </Text>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                size="md"
                bg="white"
                borderColor="gray.300"
                _hover={{ borderColor: "gray.400" }}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="a-z">Name (A-Z)</option>
                <option value="z-a">Name (Z-A)</option>
                <option value="most-jobs">Most Jobs</option>
              </Select>
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1} color="gray.600">
                Industry
              </Text>
              <Select
                value={industryFilter}
                onChange={(e) => setIndustryFilter(e.target.value)}
                size="md"
                bg="white"
                borderColor="gray.300"
                _hover={{ borderColor: "gray.400" }}
              >
                <option value="">All Industries</option>
                {industryTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Select>
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1} color="gray.600">
                Company Type
              </Text>
              <Select
                value={companyTypeFilter}
                onChange={(e) => setCompanyTypeFilter(e.target.value)}
                size="md"
                bg="white"
                borderColor="gray.300"
                _hover={{ borderColor: "gray.400" }}
              >
                <option value="">All Types</option>
                {companyTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Select>
            </Box>
          </VStack>

          {activeFilters.length > 0 && (
            <Box mt={2}>
              <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.600">
                Active Filters:
              </Text>
              <Flex gap={2} flexWrap="wrap">
                {activeFilters.map((filter, index) => (
                  <Tag key={index} size="md" borderRadius="full" variant="subtle" colorScheme="blue" py={1} px={3}>
                    <TagLabel fontWeight="medium">{filter.label}</TagLabel>
                    <TagCloseButton onClick={() => removeFilter(filter.type)} />
                  </Tag>
                ))}
              </Flex>
            </Box>
          )}
        </Flex>
      </Box>
    )
  }

  const FilterDrawer = () => (
    <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">Filter Companies</DrawerHeader>
        <DrawerBody>
          <Stack spacing={4} mt={4}>
            <Box>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                Sort By
              </FormLabel>
              <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} size="md" borderColor="gray.300">
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="a-z">Name (A-Z)</option>
                <option value="z-a">Name (Z-A)</option>
                <option value="most-jobs">Most Jobs</option>
              </Select>
            </Box>

            <Box>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                Industry
              </FormLabel>
              <Select
                value={industryFilter}
                onChange={(e) => setIndustryFilter(e.target.value)}
                size="md"
                borderColor="gray.300"
              >
                <option value="">All Industries</option>
                {industryTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Select>
            </Box>

            <Box>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                Company Type
              </FormLabel>
              <Select
                value={companyTypeFilter}
                onChange={(e) => setCompanyTypeFilter(e.target.value)}
                size="md"
                borderColor="gray.300"
              >
                <option value="">All Types</option>
                {companyTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Select>
            </Box>

            {activeFilters.length > 0 && (
              <Box mt={2}>
                <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
                  Active Filters:
                </Text>
                <Flex gap={2} flexWrap="wrap">
                  {activeFilters.map((filter, index) => (
                    <Tag key={index} size="md" borderRadius="full" variant="subtle" colorScheme="blue" py={1} px={3}>
                      <TagLabel fontWeight="medium">{filter.label}</TagLabel>
                      <TagCloseButton onClick={() => removeFilter(filter.type)} />
                    </Tag>
                  ))}
                </Flex>
              </Box>
            )}

            {activeFilters.length > 0 && (
              <Button
                mt={4}
                size="sm"
                variant="outline"
                colorScheme="blue"
                onClick={clearAllFilters}
                leftIcon={<FaFilter />}
              >
                Clear All Filters
              </Button>
            )}
          </Stack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )

  const bgColor = useColorModeValue("gray.50", "gray.900")
  const headerBg = useColorModeValue("blue.600", "blue.900")
  const headerTextColor = useColorModeValue("white", "gray.100")
  const subTextColor = useColorModeValue("blue.100", "blue.200")

  return (
    <Box bg={bgColor} minH="100vh">
      <Box
        className="bg-[linear-gradient(to_bottom_right,_#1565C0,_#1E88E5,_#26C6DA)]"
        py={{ base: 8, md: 10, lg: 12 }}
        mb={4}
      >
        <Box width="100%" mx="auto" px={{ base: 4, md: 6, lg: 8 }}>
          <VStack spacing={6} align="start">
            <Heading as="h1" size="2xl" color={headerTextColor}>
              Explore Top Companies
            </Heading>
            <Text fontSize="xl" color={subTextColor}>
              Discover opportunities at leading organizations
            </Text>
          </VStack>
        </Box>
      </Box>

      <Box width="100%" mx="auto" px={{ base: 4, md: 6, lg: 8 }} py={{ base: 6, md: 8, lg: 10 }}>
        <Box display={{ base: "block", lg: "none" }} mb={4}>
          <Button leftIcon={<FaFilter />} colorScheme="blue" variant="outline" onClick={onOpen} width="full">
            Filter Companies
          </Button>
        </Box>

        <FilterDrawer />

        <Flex direction={{ base: "column", lg: "row" }} gap={6}>
          <Box width={{ base: "100%", lg: "300px" }} display={{ base: "none", lg: "block" }}>
            <FilterSidebar />
          </Box>

          <Box flex="1">
            <Flex
              justify="space-between"
              align={{ base: "flex-start", md: "center" }}
              mb={4}
              direction={{ base: "column", md: "row" }}
              gap={3}
            >
              <Text fontSize="sm" color="gray.600">
                {filteredCompanies?.length > 0
                  ? `Showing ${filteredCompanies.length} of ${totalCompanies} companies`
                  : ""}
              </Text>

              <InputGroup maxW={{ base: "100%", md: "300px" }}>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search companies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  borderRadius="md"
                  bg="white"
                  borderColor="gray.300"
                  _hover={{ borderColor: "gray.400" }}
                />
              </InputGroup>
            </Flex>

            {activeFilters.length > 0 && (
              <Box display={{ base: "block", lg: "none" }} mb={4}>
                <Flex gap={2} flexWrap="wrap">
                  {activeFilters.map((filter, index) => (
                    <Tag key={index} size="md" borderRadius="full" variant="subtle" colorScheme="blue" py={1} px={3}>
                      <TagLabel fontWeight="medium">{filter.label}</TagLabel>
                      <TagCloseButton onClick={() => removeFilter(filter.type)} />
                    </Tag>
                  ))}
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="blue"
                    onClick={clearAllFilters}
                    leftIcon={<FaFilter />}
                  >
                    Clear All
                  </Button>
                </Flex>
              </Box>
            )}

            {companies ? (
              <>
                <SimpleGrid columns={columns} spacing={3}>
                  {(filteredCompanies?.length > 0 ? filteredCompanies : companies).map((company) => (
                    <CompanyCard
                      key={company.uuid}
                      company={company}
                      base_url={EMPLOYER_COMPANY_LOGO_SAVE_URL}
                      onViewJobs={handleViewJobs}
                    />
                  ))}
                </SimpleGrid>
                <div className="flex justify-evenly items-center mt-4">
                  <button
                    className={`bg-gray-300 text-black px-4 py-2 rounded ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    className={`bg-blue-500 text-white px-4 py-2 rounded ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </>
            ) : (
              <SimpleGrid columns={columns} spacing={8}>
                {[...Array(6)].map((_, index) => (
                  <CompanySkeleton key={index} />
                ))}
              </SimpleGrid>
            )}
          </Box>
        </Flex>
      </Box>


    </Box>
  )
}